import { useState, useEffect } from 'react';
import { z } from 'zod';
import { logger } from '@/lib/logger';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LoaderIcon, ShieldIcon, AgentsIcon } from '@/config/icons';
import { AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useWorkspaceContext } from '@/contexts/WorkspaceContext';
import { useProfile } from '@/hooks/useProfile';
import { supabase } from '@/integrations/supabase/client';
import { Link } from 'react-router-dom';

type AgentRole = 'admin' | 'super_agent' | 'agent';

const emailSchema = z.string().email("Adresse email invalide");

const roleConfig: Record<AgentRole, { label: string; icon: React.ReactNode }> = {
  admin: { label: 'Administrateur', icon: <ShieldIcon className="h-3 w-3" /> },
  super_agent: { label: 'Super Agent', icon: <AgentsIcon className="h-3 w-3" /> },
  agent: { label: 'Agent', icon: <AgentsIcon className="h-3 w-3" /> },
};

const editableRoles: AgentRole[] = ['admin', 'super_agent', 'agent'];

interface InviteAgentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onInviteSent?: () => void;
}

export function InviteAgentDialog({ open, onOpenChange, onInviteSent }: InviteAgentDialogProps) {
  const { toast } = useToast();
  const { activeWorkspace } = useWorkspaceContext();
  const { profile } = useProfile();
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<AgentRole>('agent');
  const [inviting, setInviting] = useState(false);
  const [canAddAgent, setCanAddAgent] = useState<boolean | null>(null);
  const [checkingQuota, setCheckingQuota] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Check quota when dialog opens
  useEffect(() => {
    if (open && activeWorkspace) {
      const checkQuota = async () => {
        setCheckingQuota(true);
        try {
          const { data, error } = await supabase.rpc('can_add_agent', {
            _workspace_id: activeWorkspace.id,
          });
          if (error) {
            logger.error("Failed to check agent quota", error, "InviteAgentDialog");
            setCanAddAgent(true); // Allow attempt if check fails
          } else {
            setCanAddAgent(data);
          }
        } catch (err) {
          logger.error("Failed to check agent quota", err, "InviteAgentDialog");
          setCanAddAgent(true);
        } finally {
          setCheckingQuota(false);
        }
      };
      checkQuota();
    }
  }, [open, activeWorkspace]);

  const handleInviteAgent = async () => {
    if (!inviteEmail.trim() || !activeWorkspace) return;

    // Validate email format
    const emailValidation = emailSchema.safeParse(inviteEmail.trim());
    if (!emailValidation.success) {
      setEmailError("Veuillez entrer une adresse email valide");
      return;
    }

    // Double-check quota before sending
    if (canAddAgent === false) {
      toast({
        title: 'Limite atteinte',
        description: 'Vous avez atteint votre limite d\'agents. Passez à une offre supérieure.',
        variant: 'destructive',
      });
      return;
    }

    setInviting(true);

    try {
      const inviterName = profile?.first_name && profile?.last_name
        ? `${profile.first_name} ${profile.last_name}`
        : profile?.first_name || 'Un membre de l\'équipe';

      const { data, error } = await supabase.functions.invoke('send-agent-invitation', {
        body: {
          emails: [inviteEmail.trim().toLowerCase()],
          workspaceId: activeWorkspace.id,
          workspaceName: activeWorkspace.name,
          inviterName,
          role: inviteRole,
        },
      });

      if (error) {
        throw error;
      }

      if (data?.results?.[0]?.success) {
        toast({
          title: 'Invitation envoyée',
          description: `Un email d'invitation a été envoyé à ${inviteEmail}.`,
        });
        onInviteSent?.();
        onOpenChange(false);
        setInviteEmail('');
        setInviteRole('agent');
      } else {
        const errorMessage = data?.results?.[0]?.error || 'Erreur lors de l\'envoi';
        toast({
          title: 'Impossible d\'envoyer l\'invitation',
          description: errorMessage,
          variant: 'destructive',
        });
        setInviting(false);
        return; // Don't close dialog, let user correct
      }
    } catch (error: any) {
      logger.error("Failed to send invitation", error, "InviteAgentDialog");
      toast({
        title: 'Erreur',
        description: error.message || 'Impossible d\'envoyer l\'invitation.',
        variant: 'destructive',
      });
    } finally {
      setInviting(false);
    }
  };

  const quotaReached = canAddAgent === false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Inviter un agent</DialogTitle>
          <DialogDescription>
            Ajoutez un nouveau membre à votre équipe. Un email d'invitation sera envoyé.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {quotaReached && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription className="ml-2">
                Vous avez atteint votre limite d'agents.{' '}
                <Link to="/offer" className="underline font-medium hover:opacity-80">
                  Passez à une offre supérieure →
                </Link>
              </AlertDescription>
            </Alert>
          )}
          <div className="space-y-2">
            <Label htmlFor="invite-email">Email</Label>
            <Input
              id="invite-email"
              type="email"
              placeholder="agent@exemple.com"
              value={inviteEmail}
              onChange={(e) => {
                setInviteEmail(e.target.value);
                setEmailError('');
              }}
              disabled={quotaReached}
              aria-invalid={!!emailError}
            />
            {emailError && (
              <p className="text-xs text-destructive mt-1">{emailError}</p>
            )}
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">Rôle</label>
            <Select 
              value={inviteRole} 
              onValueChange={(v) => setInviteRole(v as AgentRole)}
              disabled={quotaReached}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {editableRoles.map((role) => (
                  <SelectItem key={role} value={role}>
                    <div className="flex items-center gap-2">
                      {roleConfig[role].icon}
                      {roleConfig[role].label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button 
            onClick={handleInviteAgent} 
            disabled={!inviteEmail.trim() || inviting || checkingQuota || quotaReached}
          >
            {(inviting || checkingQuota) ? <LoaderIcon className="h-4 w-4 animate-spin mr-2" /> : null}
            {quotaReached ? 'Limite atteinte' : 'Envoyer l\'invitation'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
